import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { isFeatureActive } from '@/lib/featureFlags';
import type { ConfigData } from '@/types/app';
import { FeatureFlag } from '@/types/app';
import Card from './Card';

interface ExplainerStep {
	title: string;
	description: string;
}

interface AppContextType {
	config?: ConfigData;
}

const Explainer = (): React.JSX.Element | null => {
	const { t } = useTranslation();
	const context = useAppContext() as AppContextType | null;
	const config = context?.config;

	const steps: ExplainerStep[] = [
		{
			title: t('policy.explainer.step1.title'),
			description: t('policy.explainer.step1.description')
		},
		{
			title: t('policy.explainer.step2.title'),
			description: t('policy.explainer.step2.description')
		},
		{
			title: t('policy.explainer.step3.title'),
			description: t('policy.explainer.step3.description')
		},
		{
			title: t('policy.explainer.step4.title'),
			description: t('policy.explainer.step4.description')
		}
	];

	if (!isFeatureActive(config, FeatureFlag.ENABLE_LANDING_PAGE_EXPLAINER)) {
		return null;
	}

	return (
		<div data-testid="feature-explainer">
			<div className="w-full">
				<h4 className="mb-10 text-left text-3xl font-bold leading-tight tracking-tight text-card-foreground sm:text-center">
					{t('policy.explainer.howItWorks')}
				</h4>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
					{steps.map((step, index) => (
						<div key={step.title}>
							<Card
								stepLabel={t('policy.explainer.stepLabel', { number: index + 1 })}
								title={step.title}
								description={step.description}
							/>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default Explainer;
